import { axiosInstance } from "./axios";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import { TwoFactorMethod } from "dto/Settings/TwoFactorMethod";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IInitAppState } from "dto/Settings/IInitAppState";
import { IContactForm } from "dto/Settings/IContactForm";
import { IFooter } from "dto/Settings/IFooter";
import { ENotificationChannel } from "dto/Settings/INotificationSettingsReponse";
import { IIntegrationsResponse } from "dto/Settings/IIntegrationsResponse";
import { IPublicAddressBookStatus } from "dto/Settings/IPublicAddressBookStatus";

const queryKeys = {
  getTwoFactorSettings: "settingsService.get2faSettings",
  initTotp: "settingsService.initTotp",
  getNotificationSettings: "settingsService.getNotificationSettings",
  getAvatar: "settingsService.getAvatar",
  getSpaceUsage: "settingsService.getSpaceUsage",
  getIntegrationSettings: "settingsService.getIntegrationSettings",
  getCalendarSettings: "settingsService.getCalendarSettings",
  getCourtPortalSettings: "settingsService.getCourtPortalSettings",
  getKsefSettings: "settingsService.getKsefSettings",
  getCourtPortalCaseSettings: "settingsService.getCourtPortalCaseSettings",
  getMsRedirect: "settingsService.getMsRedirect",
  getTrustedBrowsers: "settingsService.getTrustedBrowsers",
  getPhoneNumber: "settingsService.getPhoneNumber",
  getSettings: "settingsService.getSettings",
  getForm: "settingsService.getForm",
  getInitState: "settingsService.getInitState",
  getFooter: "settingsService.getFooter",
  getPublicAddressBookStatus: "settingsService.getPublicAddressBookStatus",
  getAliases: "settingsService.getAliases",
  getUserAccesses: "settingsService.getUserAccesses",
};

const get2FaSettings = () => {
  return axiosInstance.get(`/2fa`);
};

const useGet2FaSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getTwoFactorSettings,
    () => get2FaSettings(),
    {
      enabled: !disabled,
    },
  );
};

export interface IFidoMakeCredentialOptionsRequest {
  attType: "none" | "direct" | "indirect";
  authType: "" | "platform" | "cross-platform";
  requireResidentKey: boolean;
  userVerification: "preferred" | "required" | "discouraged";
}

const fidoMakeCredentialOptions = (
  payload: IFidoMakeCredentialOptionsRequest,
) => {
  return axiosInstance.post("/2fa/fido/pwmakeCredentialOptions", payload);
};

const useFidoMakeCredentialOptions = () => {
  return useCustomMutation(fidoMakeCredentialOptions);
};

const fidoMakeCredential = (payload: any) => {
  return axiosInstance.post("/2fa/fido/pwmakeCredential", payload);
};

const useFidoMakeCredential = () => {
  return useCustomMutation(fidoMakeCredential);
};

const getInitTotp = () => {
  return axiosInstance.get(`/2fa/totp`);
};

const useGetInitTotp = () => {
  return useCustomMutation(getInitTotp);
};

export interface IConfigureTotpRequest {
  code: string;
}

const setTotp = (payload: IConfigureTotpRequest) => {
  return axiosInstance.post("2fa/totp", payload);
};

const useSetTotp = () => {
  return useCustomMutation(setTotp);
};

const getNotificationSettings = () => {
  return axiosInstance.get(`/settings/notifications`);
};

const useGetNotificationSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getNotificationSettings,
    () => getNotificationSettings(),
    {
      enabled: !disabled,
    },
  );
};

export interface ISetNotification {
  Id: string;
  ActiveChannel: ENotificationChannel;
}

const setNotification = (payload: ISetNotification) => {
  return axiosInstance.post("/settings/notification", payload);
};

const useSetNotification = () => {
  return useCustomMutation(setNotification);
};

export interface IChangePassword {
  OldPassword: string;
  NewPassword: string;
  RepeatPassword: string;
}

const setChangePassword = (payload: IChangePassword) => {
  return axiosInstance.post("/settings/password", payload);
};

const useSetPassword = () => {
  return useCustomMutation(setChangePassword);
};

const uploadAvatar = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  return axiosInstance.post(`/settings/avatar`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const getAvatar = () => {
  return axiosInstance.get(`/settings/avatar`, {
    responseType: "blob",
  });
};

const useGetAvatar = () => {
  return useCustomMutation(getAvatar, {
    retry: false,
  });
};

const useUploadAvatar = () => {
  return useCustomMutation(uploadAvatar);
};

const getSpaceUsage = () => {
  return axiosInstance.get(`settings/space`);
};

const useGetSpaceUsage = (disabled: boolean = false) => {
  return useCustomQuery(queryKeys.getSpaceUsage, () => getSpaceUsage(), {
    enabled: !disabled,
  });
};

const getIntegrationSettings = (): Promise<{ data: IIntegrationsResponse }> => {
  return axiosInstance.get(`settings/integrations`);
};

const useGetIntegrationSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getIntegrationSettings,
    () => getIntegrationSettings(),
    {
      enabled: !disabled,
    },
  );
};

export enum MailServerType {
  Imap = 0,
  Pop3 = 1,
}

export interface IServerConfiguration {
  IncomingHost: string;
  IncomingPort: number;
  IncomingSecurity: number;
  OutgoingHost: string;
  OutgoingPort: number;
  OutgoingSecurity: number;
  IncomingType: MailServerType;
}

export enum EIntegrationHistoryTimespan {
  Month1 = 1,
  Month3 = 3,
  Month6 = 6,
  Year = 12,
  Year2 = 24,
  Year3 = 36,
  Unlimited = 99,
}

export interface IConfigureIntegrationRequest {
  Type: IntegrationType;
  Provider: IntegrationProvider;
  Code?: string;
  Scope?: string;
  Token?: string;
  Email?: string;
  Label?: string;
  ImapLogin?: string;
  ImapPassword?: string;
  ServerConfiguration?: IServerConfiguration;

  AppleLogin?: string;
  ApplePassword?: string;

  PublicKey?: string;
  PrivateKey?: string;
  /** Hasło do zaszyfrowanego klucza prywatnego (KSeF, ENCRYPTED PRIVATE KEY). */
  PrivateKeyPassword?: string;

  Nip?: string;

  HistoryRange?: number;

  // Rządowe e-Doręczenia (Poczta Polska UA API)
  /** Adres do doręczeń elektronicznych (ADE), np. "AE:PL-...". */
  EDeliveryAddress?: string;
  /** Nazwa systemu (składnik issuera JWT). */
  SystemName?: string;
  /** Certyfikat klienta (PFX/P12) zakodowany base64. */
  Pfx?: string;
  /** Hasło do certyfikatu PFX. */
  PfxPassword?: string;
  /** Częstotliwość synchronizacji w sekundach (domyślnie 1800). */
  SyncInterval?: number;

  // ePUAP (WCF/SOAP)
  /** Identyfikator podmiotu ePUAP (EntityEID). */
  EpuapEntityEID?: string;
  /** Adres skrytki ePUAP (bez prefiksu podmiotu). */
  EpuapMessageBoxAddress?: string;
  /** Nazwa skrytki (wyświetlana, opcjonalna). */
  EpuapMessageBoxName?: string;

  // UPE (Urzędowa Poczta Elektroniczna — wnioski o publikację w dzienniku urzędowym)
  /** Nazwa systemu wystawcy dziennika, np. "epoczta3_uwp". */
  UpeSystemOfficeName?: string;
  /** Login do UPE. */
  UpeLogin?: string;
  /** Hasło do UPE — szyfrowane po stronie serwera, nigdy nie wraca do klienta. */
  UpePassword?: string;
  /** Adres usługi wpisany ręcznie; puste = wyliczony z wystawcy. */
  UpeServiceUrl?: string;
}

const configureIntegration = (payload: IConfigureIntegrationRequest) => {
  return axiosInstance.post("/settings/integration", payload);
};

const useConfigureIntegration = () => {
  return useCustomMutation(configureIntegration);
};

export interface IRemoveIntegrationRequest {
  Id: number;
}

const removeIntegration = (payload: IRemoveIntegrationRequest) => {
  return axiosInstance.delete("/settings/integration", { data: payload });
};

const useRemoveIntegration = () => {
  return useCustomMutation(removeIntegration);
};

export interface IMoveIntegrationRequest {
  /** Id integracji (UserExternalIntegration). */
  Id: number;
  /** Profil docelowy. */
  ProfileId: string;
  /**
   * Klucz prywatny integracji przeszyfrowany kluczem publicznym profilu docelowego
   * (base64). BE mapuje to na byte[] i zapisuje jako nowy klucz integracji,
   * zachowując poprzedni w historii migracji. Wymagany tylko dla integracji z kluczem
   * związanym z profilem (e-mail); np. CourtPortal nie ma takiego klucza i pomija pole.
   */
  PrivateKey?: string;
}

const moveIntegrationToProfile = (payload: IMoveIntegrationRequest) => {
  return axiosInstance.post("/settings/integration/move", payload);
};

const useMoveIntegrationToProfile = () => {
  return useCustomMutation(moveIntegrationToProfile);
};

/**
 * Rodzaj podmiotu faktury KSeF (InvoiceSubjectType): 0 - sprzedaż, 1 - zakup,
 * 2 - inny podmiot, 3 - podmiot upoważniony.
 */
export interface IKSeFSubjectCount {
  SubjectType: number;
  Count: number;
}

export interface IKSeFSettings {
  Nip?: string;
  LastSync?: string;
  /** Ważność środka autoryzacji KSeF; brak = autoryzacja bezterminowa. */
  AuthValidUntil?: string;
  SubjectCounts: IKSeFSubjectCount[];
}

const getKsefSettings = (): Promise<{ data: IKSeFSettings }> => {
  return axiosInstance.get(`ksef/settings`);
};

const useGetKsefSettings = (disabled: boolean = false) => {
  return useCustomQuery(queryKeys.getKsefSettings, () => getKsefSettings(), {
    enabled: !disabled,
  });
};

const getCalendarSettings = () => {
  return axiosInstance.get(`calendar/settings`);
};

const useGetCalendarSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getCalendarSettings,
    () => getCalendarSettings(),
    {
      enabled: !disabled,
    },
  );
};

export interface ISetCalendarSettings {
  defaultCalendarId: string;
}

const setCalendarSettings = (payload: ISetCalendarSettings) => {
  return axiosInstance.post("calendar/settings", payload);
};

const useSetCalendarSettings = () => {
  return useCustomMutation(setCalendarSettings);
};

const getCourtPortalSettings = () => {
  return axiosInstance.get(`courtPortal/settings`);
};

const useGetCourtPortalSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getCourtPortalSettings,
    () => getCourtPortalSettings(),
    {
      enabled: !disabled,
    },
  );
};

const getCourtPortalCaseSettings = (): Promise<{
  data: ICourtPortalCaseSettings;
}> => {
  return axiosInstance.get(`courtPortal/case/settings`);
};

const useGetCourtPortalCaseSettings = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getCourtPortalCaseSettings,
    () => getCourtPortalCaseSettings(),
    {
      enabled: !disabled,
    },
  );
};

export interface ICourtPortalCaseSettings {
  SyncActive: boolean;
  SyncClosed: boolean;
}

const setCaseSettings = (payload: ICourtPortalCaseSettings) => {
  return axiosInstance.post("courtPortal/case/settings", payload);
};

const useSetCaseSettings = () => {
  return useCustomMutation(setCaseSettings);
};

export interface ICourtPortalLoginRequest {
  Login: string;
  Password: string;
  PortalIds: number[];
}

const loginCourtPortal = (payload: ICourtPortalLoginRequest) => {
  return axiosInstance.post("courtPortal/login", payload);
};

const useLoginCourtPortal = () => {
  return useCustomMutation(loginCourtPortal);
};

export interface ICourtPortalConfirmRequest {
  Login: string;
}

const confirmCourtPortal = (payload: ICourtPortalConfirmRequest) => {
  return axiosInstance.post("courtPortal/confirm", payload);
};

const useConfirmCourtPortal = () => {
  return useCustomMutation(confirmCourtPortal);
};

export interface IUpdateCourtPortalSettingsRequest {
  PortalIds: Array<number>;
  DocumentIds: Array<number>;
}

const updateCourtPortalSettings = (
  payload: IUpdateCourtPortalSettingsRequest,
) => {
  return axiosInstance.post("courtPortal/settings", payload);
};

const useUpdateCourtPortalSettings = () => {
  return useCustomMutation(updateCourtPortalSettings);
};

const getMsRedirect = (payload: { scopes: string }) => {
  const query = new URLSearchParams(window.location.search);

  if (payload.scopes?.length) {
    query.append("scopes", payload.scopes);
  }

  return axiosInstance.get(`settings/integration/ms?${query}`);
};

const useGetMsredirect = () => {
  return useCustomMutation(getMsRedirect, {
    retry: false,
  });
};

const getTrustedBrowsers = () => {
  return axiosInstance.get(`/trustedBrowsers`);
};

const useGetTrustedBrowsers = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getTrustedBrowsers,
    () => getTrustedBrowsers(),
    {
      enabled: !disabled,
    },
  );
};

export interface IRemoveTrustedBrowser {
  Id: number;
}

const removeTrustedBrowser = (payload: IRemoveTrustedBrowser) => {
  return axiosInstance.delete("/trustedBrowser", { data: payload });
};

const useRemoveTrustedBrowser = () => {
  return useCustomMutation(removeTrustedBrowser);
};

export interface IRemoveTwoFactorMethod {
  method: TwoFactorMethod;
}

const remove2faMethod = (payload: IRemoveTwoFactorMethod) => {
  return axiosInstance.delete("/2fa", { data: payload });
};

const useRemove2faMethod = () => {
  return useCustomMutation(remove2faMethod);
};

const getPhoneNumber = () => {
  return axiosInstance.get("/settings/personal");
};

const useGetPhoneNumber = () => {
  return useCustomQuery(queryKeys.getPhoneNumber, () => getPhoneNumber());
};

export interface ISetPersonal {
  Phone: string;
}

const setPersonal = (payload: ISetPersonal) => {
  return axiosInstance.post("/settings/personal", payload);
};

const useSetPersonal = () => {
  return useCustomMutation(setPersonal);
};

const getSettings = () => {
  return axiosInstance.get("/settings");
};

const useGetSettings = () => {
  return useCustomQuery(queryKeys.getSettings, () => getSettings());
};

export interface IUpdateSetting {
  Type: string;
  Value: string;
}

const updateSetting = (payload: IUpdateSetting) => {
  return axiosInstance.post("/settings/setting", payload);
};

const useUpdateSetting = () => {
  return useCustomMutation(updateSetting);
};

const getForm = (workspaceId?: string): Promise<{ data: IContactForm[] }> => {
  if (workspaceId)
    return axiosInstance.get(
      `/settings/contactForms?workspaceId=${workspaceId}`,
    );
  return axiosInstance.get("/settings/contactForms");
};

const useGetForm = (workspaceId?: string) => {
  // Formularz kontaktowy jest scope'owany PER FIRMĘ.
  return useCustomQuery([queryKeys.getForm, workspaceId ?? ""], () =>
    getForm(workspaceId),
  );
};

export interface IContactFormInvitation {
  Name: string;
  Email: string;
}

export type ContactFormFieldStyle = "outlined" | "filled" | "standard";
export type ContactFormPublishMethod = "link" | "iframe" | "eszyfrowane";

export interface IUpdateContactForm {
  WorkspaceId?: string;
  Theme: number;
  RegistryOfficeUserIds?: string[];
  ReceiverIds?: string[];
  WorkspaceMemberIds?: string[];

  Invitations?: IContactFormInvitation[];
  BackgroundColor?: string;
  AccentColor?: string;
  FieldStyle?: ContactFormFieldStyle;
  PublishMethod?: ContactFormPublishMethod;
  HeaderTitle?: string;
  ShowHeader?: boolean;
}

const updateContactForm = (payload: IUpdateContactForm) => {
  return axiosInstance.post("/settings/contactForm", payload);
};

const useUpdateContactForm = () => {
  return useCustomMutation(updateContactForm);
};

const uploadContactFormLogo = (payload: { key: string; file: File }) => {
  const formData = new FormData();
  formData.append("file", payload.file);

  return axiosInstance.post(
    `/settings/contactForm/logo?key=${payload.key}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

const getContactFromLogo = (payload: { key: string }) => {
  return axiosInstance.get(`/settings/contactForm/logo?key=${payload.key}`, {
    responseType: "blob",
  });
};

const useGetContactFormLogo = () => {
  return useCustomMutation(getContactFromLogo, {
    retry: false,
  });
};

const useUploadContactFormLogo = () => {
  return useCustomMutation(uploadContactFormLogo);
};

const deleteContactFormLogo = (payload: { key: string }) => {
  return axiosInstance.delete(`/settings/contactForm/logo?key=${payload.key}`);
};

const useDeleteContactFormLogo = () => {
  return useCustomMutation(deleteContactFormLogo);
};

const setInitialConfigurationCompleted = () => {
  return axiosInstance.post("/settings/init/completed");
};

const useSetInitialConfigurationCompleted = () => {
  return useCustomMutation(setInitialConfigurationCompleted);
};

const getInitialState = (): Promise<{ data: IInitAppState }> => {
  return axiosInstance.get(`/settings/init`);
};

const useGetInitialState = (disabled: boolean = false) => {
  return useCustomQuery(queryKeys.getInitState, () => getInitialState(), {
    enabled: !disabled,
  });
};

const setFooter = (payload: IFooter) => {
  return axiosInstance.post("/settings/footer", payload);
};

const useSetFooter = () => {
  return useCustomMutation(setFooter);
};

const getFooter = (): Promise<{ data: IFooter }> => {
  return axiosInstance.get(`/settings/footer`);
};

const useGetFooter = (disabled: boolean = false) => {
  return useCustomQuery(queryKeys.getFooter, () => getFooter(), {
    retry: false,
    enabled: !disabled,
  });
};

const getPublicAddressBookStatus = (): Promise<{
  data: IPublicAddressBookStatus;
}> => {
  return axiosInstance.get(`settings/publicAddressBook`);
};

const useGetPublicAddressBookStatus = (disabled: boolean = false) => {
  return useCustomQuery(
    queryKeys.getPublicAddressBookStatus,
    () => getPublicAddressBookStatus(),
    {
      retry: 0,
      enabled: !disabled,
      cacheTime: 0,
    },
  );
};

export interface IAddToPublicAddressBookRequest {
  WorkspaceId?: string;
  Nip: string;
  ShareAddress: boolean;

  SharePublic: boolean;
  ShareInternal: boolean;

  RegistryOfficeUserIds: string[];

  // Karta firmowa => ręczna weryfikacja reprezentacji (KRS); JDG => auto-akcept po dopasowaniu imienia.
  // Opcjonalne: BE traktuje brak jako false (osoba fizyczna). Kreator (Step7) zawsze podaje jawnie.
  IsLegalPerson?: boolean;

  // Dane firmy wpisywane przez użytkownika (prefill z Workspace.Company) — używane dla karty firmowej.
  CompanyName?: string;
  Krs?: string;
  Regon?: string;
}

const addToPublicAddressBook = (payload: IAddToPublicAddressBookRequest) => {
  return axiosInstance.post("/settings/publicAddressBook", payload, {});
};

const useAddToPublicAddressBook = () => {
  return useCustomMutation(addToPublicAddressBook);
};

export interface IRemoveFromPublicAddressBook {
  Id: string;
}

const removeFromPublicAddressBook = (payload: IRemoveFromPublicAddressBook) => {
  return axiosInstance.delete("/settings/publicAddressBook", {
    data: payload,
  });
};

const useRemoveFromPublicAddressBook = () => {
  return useCustomMutation(removeFromPublicAddressBook);
};

const getAliases = (id: number): Promise<{ data: string[] }> => {
  return axiosInstance.get(`/email/aliases?id=${id}`);
};

const useGetAliases = (id: number, disabled: boolean = false) => {
  return useCustomQuery(queryKeys.getAliases, () => getAliases(id), {
    retry: false,
    enabled: !disabled,
  });
};

export interface ICreateEmailAlias {
  Id?: number;
  Alias: string;
}

const addEmailAlias = (payload: ICreateEmailAlias) => {
  return axiosInstance.post("/email/alias", payload, {});
};

const useAddEmailAlias = () => {
  return useCustomMutation(addEmailAlias);
};

export interface IRemovelAlias {
  Id?: number;
  Alias: string;
}

const removeEmailAlias = (payload: ICreateEmailAlias) => {
  return axiosInstance.delete("/email/alias", {
    data: payload,
  });
};

const useRemoveEmailAlias = () => {
  return useCustomMutation(removeEmailAlias);
};

const getEmailUserAcesses = (id: number): Promise<{ data: string[] }> => {
  return axiosInstance.get(`/email/accesses?id=${id}`);
};

const useGetUserAccesses = (id: number, disabled: boolean = false) => {
  console.log("Get access, disabled", disabled);
  return useCustomQuery(
    queryKeys.getUserAccesses,
    () => getEmailUserAcesses(id),
    {
      retry: false,
      enabled: !disabled,
    },
  );
};

export interface ICreateIntegrationAccess {
  Id: number;
  UserId: string;
  PrivateKey: string;
}

const createIntegrationAccess = (payload: ICreateIntegrationAccess) => {
  return axiosInstance.post("/email/access", payload, {});
};

const useCreateIntegrationAccess = () => {
  return useCustomMutation(createIntegrationAccess);
};

export interface IRemoveIntegrationAccess {
  Id: number;
  UserId: string;
}

const removeIntegrationAccess = (payload: IRemoveIntegrationAccess) => {
  return axiosInstance.post("/email/access", payload, {});
};

const useRemoveIntegrationAccess = () => {
  return useCustomMutation(removeIntegrationAccess);
};

export default {
  useGet2FaSettings,
  useFidoMakeCredentialOptions,
  useFidoMakeCredential,
  useGetInitTotp,
  useSetTotp,
  useGetNotificationSettings,
  useSetNotification,
  useSetPassword,
  useUploadAvatar,
  useGetAvatar,
  useGetSpaceUsage,
  useGetIntegrationSettings,
  useConfigureIntegration,
  useGetCalendarSettings,
  useSetCalendarSettings,
  useGetCourtPortalSettings,
  useLoginCourtPortal,
  useGetMsredirect,
  useConfirmCourtPortal,
  useUpdateCourtPortalSettings,
  useRemoveIntegration,
  useMoveIntegrationToProfile,
  useGetKsefSettings,
  useGetTrustedBrowsers,
  useRemoveTrustedBrowser,
  useRemove2faMethod,
  useGetPhoneNumber,
  useSetPersonal,
  useGetSettings,
  useUpdateSetting,
  useGetForm,
  useUpdateContactForm,
  useGetCourtPortalCaseSettings,
  useSetCaseSettings,
  useSetInitialConfigurationCompleted,
  useGetInitialState,
  useGetContactFormLogo,
  useUploadContactFormLogo,
  useDeleteContactFormLogo,
  useSetFooter,
  useGetFooter,
  useGetPublicAddressBookStatus,
  useAddToPublicAddressBook,
  useRemoveFromPublicAddressBook,
  useGetAliases,
  useAddEmailAlias,
  useRemoveEmailAlias,

  useGetUserAccesses,
  useCreateIntegrationAccess,
  useRemoveIntegrationAccess,
};
